/**
 * /solo/lesson/:id — SOLO Lesson viewer.
 *
 *  Renders the lesson body + vocab + 5-Q quiz. Emits attendance heartbeat every 60s
 *  while the tab is visible. On quiz submit, marks the lesson complete with the score.
 *  If pacing engine returns locked=true, shows the "Coach B says: Rest Day" screen.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import {
  Loader2, ArrowLeft, Check, X, Lock, ChevronRight, BookOpen, Sparkles, Trophy,
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import SoloLessonVideo from '../components/SoloLessonVideo';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const SoloLesson = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState(null);
  const startRef = useRef(Date.now());
  const heartbeatRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/academy/solo/lesson/${encodeURIComponent(id)}`,
        { headers: authHeaders() });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not open lesson');
    } finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  // Attendance heartbeat — every 60s while visible & lesson is unlocked
  useEffect(() => {
    if (!data?.lesson || data.locked) return;
    const beat = () => {
      if (document.visibilityState !== 'visible') return;
      api.post('/academy/solo/attendance/heartbeat', {
        lesson_id: data.lesson.id,
        subject: data.lesson.subject,
        activity_type: quizSubmitted ? 'quiz' : 'read',
        seconds_since_last: 60,
      }, { headers: authHeaders() }).catch(() => {});
    };
    heartbeatRef.current = setInterval(beat, 60000);
    return () => clearInterval(heartbeatRef.current);
  }, [data?.lesson?.id, data?.locked, quizSubmitted]);

  if (loading) return (
    <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
    </div>
  );

  // Locked screens
  if (data?.locked) {
    if (data.reason === 'test_out_available') {
      return <TestOutGate data={data} onGo={() => navigate('/solo/test-out')}
        onBack={() => navigate('/solo/athlete')} />;
    }
    if (data.reason === 'hard_locked_recovery') {
      return <RecoveryLock data={data} onBack={() => navigate('/solo/athlete')} />;
    }
    return <RestDaySplash unlocksAt={data.unlocks_at} onBack={() => navigate('/solo/athlete')} />;
  }

  const lesson = data?.lesson;
  if (!lesson) return null;

  const quiz = lesson.quiz || [];
  const hasBody = !!lesson.body_md;

  const submitQuiz = async () => {
    if (quiz.length === 0) {
      // No quiz — mark complete directly
      await api.post('/academy/solo/lesson/complete', {
        lesson_id: lesson.id,
        time_seconds: Math.floor((Date.now() - startRef.current) / 1000),
      }, { headers: authHeaders() });
      setQuizSubmitted(true);
      toast.success('Lesson complete');
      return;
    }
    let correct = 0;
    quiz.forEach((q, i) => {
      if (quizAnswers[i] === q.answer_index) correct++;
    });
    const score = correct / quiz.length;
    setQuizScore(score);
    setQuizSubmitted(true);
    try {
      await api.post('/academy/solo/lesson/complete', {
        lesson_id: lesson.id,
        score,
        time_seconds: Math.floor((Date.now() - startRef.current) / 1000),
      }, { headers: authHeaders() });
      if (score >= 0.8) toast.success(`Mastery hit — ${Math.round(score * 100)}%`);
      else toast(`Score ${Math.round(score * 100)}% — try Film Room to retake`);
    } catch { toast.error('Could not save score'); }
  };

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white pt-16 pb-24" data-testid="solo-lesson">
      <div className="border-b-2 border-zinc-900 py-6 px-6">
        <div className="container mx-auto max-w-3xl">
          <Link to="/solo/athlete" className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500 hover:text-white flex items-center gap-1 mb-3">
            <ArrowLeft className="w-3 h-3" /> Athlete Dashboard
          </Link>
          <div className="text-[10px] tracking-[0.4em] uppercase font-black text-orange-500">{lesson.subject}</div>
          <h1 className="text-2xl md:text-4xl font-black uppercase tracking-tight leading-[1.05] mt-2">{lesson.title}</h1>
          <div className="text-[11px] text-zinc-500 mt-2">{lesson.unit_title} · {lesson.minutes_est || 20} min</div>
        </div>
      </div>

      <div className="container mx-auto max-w-3xl px-6 py-8 space-y-8">
        {/* Coach's Corner — two-host NotebookLM-style explainer video.
            Renders CTA on first open, generates on-demand, cached thereafter. */}
        {hasBody && (
          <SoloLessonVideo lessonId={lesson.id} />
        )}

        {!hasBody ? (
          <div className="border-2 border-zinc-900 bg-zinc-950 p-6" data-testid="lesson-draft">
            <div className="text-[10px] tracking-[0.4em] uppercase font-black text-orange-500 mb-2 flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> Coach B is Drafting This Lesson
            </div>
            <p className="text-sm text-zinc-300 leading-relaxed">
              This lesson's body copy is being generated. Come back in a few minutes — Unit 1 (Warm-Up through Mastery Check) is drafted first for every subject.
            </p>
          </div>
        ) : (
          <div className="max-w-none text-zinc-300 leading-relaxed space-y-3 [&_h1]:text-2xl [&_h1]:font-black [&_h1]:uppercase [&_h1]:tracking-tight [&_h1]:text-white [&_h1]:mt-4 [&_h1]:mb-2 [&_h2]:text-lg [&_h2]:font-black [&_h2]:uppercase [&_h2]:tracking-wide [&_h2]:text-orange-500 [&_h2]:mt-4 [&_h2]:mb-1 [&_h3]:text-base [&_h3]:font-black [&_h3]:text-white [&_h3]:mt-3 [&_p]:leading-relaxed [&_ul]:list-disc [&_ul]:ml-5 [&_ol]:list-decimal [&_ol]:ml-5 [&_li]:my-1 [&_strong]:text-orange-400 [&_strong]:font-black [&_code]:bg-black [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:text-orange-400 [&_code]:text-sm" data-testid="lesson-body">
            <ReactMarkdown>{lesson.body_md}</ReactMarkdown>
          </div>
        )}

        {(lesson.vocab || []).length > 0 && (
          <div className="border-2 border-zinc-900 bg-zinc-950 p-5" data-testid="lesson-vocab">
            <div className="text-[10px] tracking-[0.4em] uppercase font-black text-zinc-500 mb-3 flex items-center gap-2">
              <BookOpen className="w-3.5 h-3.5" /> Playbook Vocabulary
            </div>
            <ul className="space-y-2">
              {lesson.vocab.map((v, i) => (
                <li key={i} className="text-sm text-zinc-300 leading-relaxed">{v}</li>
              ))}
            </ul>
          </div>
        )}

        {quiz.length > 0 && (
          <div className="border-2 border-orange-500/40 bg-orange-500/5 p-5" data-testid="lesson-quiz">
            <div className="text-[10px] tracking-[0.4em] uppercase font-black text-orange-500 mb-4">Quiz · {quiz.length} Questions</div>
            <div className="space-y-6">
              {quiz.map((q, qi) => (
                <div key={qi}>
                  <div className="text-sm font-black text-white mb-3">{qi + 1}. {q.q}</div>
                  <div className="grid grid-cols-1 gap-2">
                    {(q.choices || []).map((c, ci) => {
                      const chosen = quizAnswers[qi] === ci;
                      const correct = ci === q.answer_index;
                      const showResult = quizSubmitted;
                      const isRight = showResult && chosen && correct;
                      const isWrong = showResult && chosen && !correct;
                      const revealCorrect = showResult && correct && !chosen;
                      return (
                        <button key={ci} disabled={quizSubmitted}
                          onClick={() => setQuizAnswers({ ...quizAnswers, [qi]: ci })}
                          className={`text-left px-3 py-2.5 border-2 text-sm flex items-center gap-2 transition-all ${
                            isRight ? 'border-green-500 bg-green-500/10 text-white' :
                            isWrong ? 'border-red-500 bg-red-500/10 text-white' :
                            revealCorrect ? 'border-green-500/50 text-green-400' :
                            chosen ? 'border-orange-500 bg-orange-500/10 text-white' :
                            'border-zinc-800 hover:border-zinc-600 text-zinc-300 bg-black'
                          }`}
                          data-testid={`quiz-${qi}-choice-${ci}`}>
                          {isRight || revealCorrect ? <Check className="w-3.5 h-3.5 flex-shrink-0" /> :
                            isWrong ? <X className="w-3.5 h-3.5 flex-shrink-0" /> : <span className="w-3.5" />}
                          {c}
                        </button>
                      );
                    })}
                  </div>
                  {quizSubmitted && q.explanation && (
                    <div className="text-[11px] text-zinc-400 mt-2 italic">{q.explanation}</div>
                  )}
                </div>
              ))}
            </div>

            {!quizSubmitted ? (
              <button onClick={submitQuiz}
                disabled={Object.keys(quizAnswers).length !== quiz.length}
                className="w-full bg-orange-500 hover:bg-orange-400 text-black py-3 text-[11px] font-black uppercase tracking-[0.2em] mt-6 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="quiz-submit">
                Submit Quiz
              </button>
            ) : (
              <div className="mt-6 text-center border-t-2 border-orange-500/30 pt-5">
                <div className="text-[10px] uppercase tracking-widest font-black text-zinc-500">Your Score</div>
                <div className="text-5xl font-black text-orange-500 mt-1">{Math.round((quizScore || 0) * 100)}%</div>
                <button onClick={() => navigate('/solo/athlete')}
                  className="mt-4 inline-flex items-center gap-2 bg-white hover:bg-zinc-200 text-black px-5 py-2.5 text-[11px] font-black uppercase tracking-[0.2em]"
                  data-testid="quiz-continue">
                  Back to Game Plan <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
        )}

        {quiz.length === 0 && !quizSubmitted && hasBody && (
          <button onClick={submitQuiz}
            className="w-full bg-orange-500 hover:bg-orange-400 text-black py-3 text-[11px] font-black uppercase tracking-[0.2em]"
            data-testid="mark-complete">
            Mark Lesson Complete
          </button>
        )}
      </div>
    </div>
  );
};

const RestDaySplash = ({ unlocksAt, onBack }) => (
  <div className="min-h-screen bg-gradient-to-b from-orange-500/20 via-[#0B0E14] to-[#0B0E14] text-white flex items-center justify-center px-6" data-testid="rest-day">
    <div className="text-center max-w-lg">
      <Trophy className="w-14 h-14 mx-auto text-orange-500 mb-6" />
      <div className="text-[10px] tracking-[0.5em] uppercase font-black text-orange-500 mb-3">Coach B Says</div>
      <h1 className="text-4xl md:text-6xl font-black uppercase tracking-tight leading-[1.02] mb-4">
        Nice Work
      </h1>
      <p className="text-lg text-zinc-300 mb-2">Take a rest day.</p>
      <p className="text-sm text-zinc-400 mb-8">
        You're at the 1-week work-ahead cap. This lesson unlocks {unlocksAt ? new Date(unlocksAt).toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' }) : 'Monday'}.
      </p>
      <button onClick={onBack}
        className="bg-orange-500 hover:bg-orange-400 text-black px-6 py-3 text-[11px] font-black uppercase tracking-[0.2em]"
        data-testid="rest-day-back">
        Return to the Locker
      </button>
    </div>
  </div>
);

const TestOutGate = ({ data, onGo, onBack }) => (
  <div className="min-h-screen bg-gradient-to-b from-orange-500/15 via-[#0B0E14] to-[#0B0E14] text-white flex items-center justify-center px-6" data-testid="test-out-gate">
    <div className="text-center max-w-lg">
      <div className="text-[10px] tracking-[0.5em] uppercase font-black text-orange-500 mb-3">Mastery Test-Out Available</div>
      <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tight leading-[1.05]">
        Prove It. <br /><span className="text-orange-500">Advance.</span>
      </h1>
      <p className="text-lg text-zinc-300 mt-4">You've hit the 1-week cap.</p>
      <p className="text-sm text-zinc-400 mt-4 max-w-md mx-auto">
        Score 85%+ on the cumulative Mastery Test-Out and unlock the next week block. You have <strong className="text-orange-400">{data.attempts_remaining} attempt{data.attempts_remaining === 1 ? '' : 's'}</strong> left for Milestone #{data.milestone}.
      </p>
      <div className="flex flex-wrap justify-center gap-3 mt-8">
        <button onClick={onGo}
          className="bg-orange-500 hover:bg-orange-400 text-black px-7 py-3 text-[11px] font-black uppercase tracking-[0.2em]"
          data-testid="gate-take-test">
          Take the Test-Out
        </button>
        <button onClick={onBack}
          className="border-2 border-zinc-700 hover:border-white text-white px-7 py-3 text-[11px] font-black uppercase tracking-[0.2em]"
          data-testid="gate-back">
          Back to Game Plan
        </button>
      </div>
    </div>
  </div>
);

const RecoveryLock = ({ data, onBack }) => (
  <div className="min-h-screen bg-gradient-to-b from-red-500/10 via-[#0B0E14] to-[#0B0E14] text-white flex items-center justify-center px-6" data-testid="hard-lock-recovery">
    <div className="text-center max-w-lg">
      <div className="text-[10px] tracking-[0.5em] uppercase font-black text-orange-500 mb-3">Coach B · Active Rest</div>
      <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tight leading-[1.05]">
        Recover Before You <span className="text-orange-500">Advance</span>.
      </h1>
      <p className="text-sm text-zinc-300 mt-6 max-w-md mx-auto leading-relaxed">{data.message}</p>
      <button onClick={onBack}
        className="mt-8 bg-orange-500 hover:bg-orange-400 text-black px-7 py-3 text-[11px] font-black uppercase tracking-[0.2em]"
        data-testid="recovery-back">
        Return to the Locker
      </button>
    </div>
  </div>
);

export default SoloLesson;
